import React, { useEffect, useRef, useState } from "react";

const format = value => Number(value || 0).toLocaleString();

export default function FarmGradeoutDialog({ farm, day, client, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const dialog = useRef(null);
  const closeButton = useRef(null);

  useEffect(() => {
    const previous = document.activeElement;
    closeButton.current?.focus();
    const keydown = event => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); }
      if (event.key === "Tab") {
        const buttons = [...dialog.current.querySelectorAll('button:not(:disabled)')];
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.removeEventListener("keydown", keydown); if (previous?.isConnected) previous.focus(); };
  }, [onClose]);

  // Fetch only when this dialog is mounted by clicking the Full-day total card.
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(""); setData(null);
    client.get("core/live-bunchers/gradeout/", { params: { farm, day }, signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setData(response.data); })
      .catch(e => { if (!controller.signal.aborted) setError(e.response?.data?.detail || "Unable to load the grade-out summary. Please try again."); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [client, farm, day, attempt]);

  const rows = data?.rows || [];
  const varieties = new Set(rows.map(row => row.variety)).size;
  const lengths = new Set(rows.map(row => row.length)).size;

  return <div className="lb-gradeout-backdrop" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="lb-gradeout-dialog" ref={dialog} role="dialog" aria-modal="true" aria-labelledby="lb-gradeout-title">
      <header>
        <div><p>FULL-DAY PRODUCTION</p><h2 id="lb-gradeout-title">Grade-out summary</h2><span>{farm} · {data?.day || day} · Nairobi time</span></div>
        <button ref={closeButton} type="button" onClick={onClose} aria-label="Close grade-out summary">×</button>
      </header>
      <div className="lb-gradeout-body">
        {loading ? <p role="status">Loading variety and length totals…</p> : error ? <div role="alert"><p>{error}</p><button type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button></div> : <>
          <div className="lb-gradeout-stats" aria-label="Full-day grade-out totals">
            <div><small>FULL-DAY STEMS</small><strong>{format(data?.total_stems)}</strong></div>
            <div><small>VARIETIES</small><strong>{format(varieties)}</strong></div>
            <div><small>LENGTHS</small><strong>{format(lengths)}</strong></div>
          </div>
          {rows.length ? <div className="lb-gradeout-table"><table>
            <caption>Captured stems grouped by variety and length</caption>
            <thead><tr><th scope="col">Variety</th><th scope="col">Length</th><th scope="col">Stems</th></tr></thead>
            <tbody>{rows.map((row, index) => <tr key={`${row.variety}-${row.length}-${index}`}><td>{row.variety}</td><td>{row.length}</td><td>{format(row.stems)}</td></tr>)}</tbody>
            <tfoot><tr><th colSpan="2" scope="row">Full-day total</th><td>{format(data?.total_stems)}</td></tr></tfoot>
          </table></div> : <p className="lb-gradeout-empty">No stems were captured for this farm on this day.</p>}
          <p className="lb-gradeout-stamp">Loaded at {data?.loaded_at ? new Date(data.loaded_at).toLocaleTimeString('en-GB', { timeZone: 'Africa/Nairobi' }) : "—"} · Summary does not auto-refresh</p>
        </>}
      </div>
    </section>
  </div>;
}
